import type { LocationFacetType } from '@components/app/SearchLandingPage/Hooks/useLocationFacetRefinement'
import { useLocationFacetRefinement } from '@components/app/SearchLandingPage/Hooks/useLocationFacetRefinement'

import { usePrevious } from '@components/hooks/usePrevious'
import ModalOverlay from '@components/primitives/Modal/ModalOverlay'
import debounce from 'lodash/debounce'
import {
  type ChangeEvent,
  type KeyboardEvent,
  type MouseEvent,
  type RefObject,
  useState,
} from 'react'

interface LocationAutoCompleteProps {
  inputLocation: string
  inputRef?: RefObject<HTMLInputElement>
  isLocationSelected: boolean
  setInputLocation: (location: string) => void
  setIsLocationSelected: (isLocationSelected: boolean) => void
  setLocationFacetType: (locationFacetType: LocationFacetType) => void
}

function trimCountryFromDescription(description: string) {
  return description.toString().replace(/, USA|, Canada/gi, '')
}

export default function LocationAutoComplete({
  inputLocation,
  inputRef,
  isLocationSelected,
  setInputLocation,
  setIsLocationSelected,
  setLocationFacetType,
}: LocationAutoCompleteProps) {
  const previousInputValue = usePrevious(inputLocation)

  const [locationPickerVisible, setLocationPickerVisible] = useState(false)

  const { predictions, setSearchTerm: setLocationSearchTerm } =
    useLocationFacetRefinement({
      initialLocation: '',
    })

  const inputChangeHandler = (
    event: ChangeEvent<HTMLInputElement> | MouseEvent<HTMLInputElement>,
  ) => {
    if (!locationPickerVisible) {
      setLocationPickerVisible(true)
    }
    setInputLocation(event.currentTarget.value)
    setLocationSearchTerm(event.currentTarget.value)

    if (event.currentTarget.value === '' && previousInputValue !== '') {
      setIsLocationSelected(false)
    }
  }
  // Debounce input handler to prevent excessive API calls during typing
  const debouncedInputHandler = debounce(inputChangeHandler, 250, {
    leading: true,
    trailing: false,
  })

  // Handles location selection from predictions, sets the appropriate location type,
  // updates input value and closes the dropdown
  const selectLocation = (
    prediction: google.maps.places.AutocompletePrediction,
  ) => {
    const { description, types } = prediction
    const facet: LocationFacetType = types.includes(
      'administrative_area_level_1',
    )
      ? 'state'
      : 'city'
    setLocationFacetType(facet)
    const val = trimCountryFromDescription(description)
    setInputLocation(val)
    setLocationSearchTerm(val)
    setIsLocationSelected(true)
    setLocationPickerVisible(false)
  }

  const pickFirstOptionInList = () => {
    if (!isLocationSelected && inputLocation !== '') {
      // Auto-select first prediction if user hasn't manually selected a location
      const [firstPrediction] = predictions
      if (firstPrediction) {
        selectLocation(firstPrediction)
      }
    } else if (inputLocation !== '') {
      // If location is already selected, just close the picker
      setLocationPickerVisible(false)
    }
  }

  // Handle key down events on the input
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      pickFirstOptionInList()
    }
  }

  return (
    <>
      <div className='static'>
        <input
          autoComplete='off'
          className='w-full text-ellipsis bg-transparent text-lg font-semibold leading-[180%] placeholder:text-neutral400 focus:outline-none'
          id='search-bar-location-input'
          onChange={debouncedInputHandler}
          onKeyDown={handleKeyDown}
          placeholder='Near You'
          ref={inputRef}
          type='text'
          value={inputLocation}
        />
        <ul
          className='absolute left-0 z-modalSearchPanel mx-auto my-4 w-full rounded-2xl border-2 border-neutral200 bg-white py-2 md:w-96'
          hidden={!locationPickerVisible || predictions.length === 0}
        >
          {predictions.map((item) => (
            <li
              className='mx-2 my-0 flex flex-row items-center justify-between rounded-2xl bg-white px-2 py-0 hover:cursor-pointer hover:bg-neutral50'
              key={item.description}
              // Using onMouseDown instead of onClick to ensure this fires before onBlur
              onMouseDown={(e) => {
                e.preventDefault()
                selectLocation(item)
              }}
            >
              <div className={'m-0 p-2'}>
                {trimCountryFromDescription(item.description)}
              </div>
            </li>
          ))}
        </ul>
        {/* Invisible overlay to detect clicks outside the location picker to close it */}
        <ModalOverlay
          hidden={!locationPickerVisible}
          onClick={() => setLocationPickerVisible(false)}
        />
      </div>
    </>
  )
}
